import React, { useState, useEffect } from "react";
import ButtonComponent from "./ButtonComponent";
import { useTranslation } from "react-i18next";
import { buildFinalGSTObject, getLockerNameLabelText, getUnitLabelParamsFromBool } from "../lib/Utils";
import { getSecureItemFromSpecificStorage } from "../lib/BrowserStorageAccessMiddleware";
import { LOCAL_STORAGE, LOCKER_BANK_ADDRESS, LS_TENANT_ASSIGNMENT_LOCKER_BANK_SETTINGS } from "../assets/constants/BrowserStorageKeys";
import { getFormatDate, getFormatTime } from "./TimeAndDateFormate";
import getSymbolFromCurrency from 'currency-symbol-map';

const BookingSummary = ({
  bookingLockerHandler,
  selectedLockers,
  lockerCurrency = "INR",
  handleLockerBooking = () => {},
  is_state_gst,
  isPodLockerType = false,
  bookingTimingObject = {},
  lockerName = "",
  clientRefLabel = "",
  clientRefValue = false
}) => {
  const { t } = useTranslation();
  const [updatedLockers, setUpdatedLockers] = useState(selectedLockers || []);
  const [totalPrice, setTotalPrice] = useState(0);
  const [clientRefInput, setClientRefInput] = useState("");
  const [clientRefError, setClientRefError] = useState(false);
  const isClientRefEnabled = Boolean(clientRefValue);
  const assignmentSetting = getSecureItemFromSpecificStorage(LOCAL_STORAGE, LS_TENANT_ASSIGNMENT_LOCKER_BANK_SETTINGS);

  const collectGst = assignmentSetting?.collect_gst || false;
  const cgst_rate = assignmentSetting?.cgst_rate || 0;
  const igst_rate = assignmentSetting?.igst_rate || 0;
  const sgst_rate = assignmentSetting?.sgst_rate || 0;
  const { finalGSTObject, paymentBreakdown } = buildFinalGSTObject(totalPrice, {
    collectGst,
    isStateGst: is_state_gst,
    cgstRate: cgst_rate,
    sgstRate: sgst_rate,
    igstRate: igst_rate,
    currencyCode: lockerCurrency,
  });
  const {
    baseAmount,
    totalAmount: finalTotal,
    cgstAmount,
    sgstAmount,
    igstAmount,
  } = paymentBreakdown;
  const address = getSecureItemFromSpecificStorage(LOCAL_STORAGE, LOCKER_BANK_ADDRESS) || "";
  useEffect(() => {
    setUpdatedLockers(selectedLockers || []);
  }, [selectedLockers]);

  const lockerList = updatedLockers || [];

  const handlerCLick = () => {
    bookingLockerHandler();
  };

  const handlePayment = () => {
    if (isClientRefEnabled && !clientRefInput.trim()) {
      setClientRefError(true);
      return;
    }
    handleLockerBooking(lockerList, totalPrice, finalGSTObject, isClientRefEnabled ? clientRefInput.trim() : null);
  };

  const handleClientRefChange = (e) => {
    const sanitizedValue = e.target.value.replace(/\s/g, "");
    setClientRefInput(sanitizedValue);
    if (clientRefError && sanitizedValue) {
      setClientRefError(false);
    }
  };

  useEffect(() => {
    const total = lockerList.reduce((sum, item) => {
      return (
        sum +
        Number(
          item?.selectedSlot?.price ||     // selected slot price
          item?.durations?.[0]?.price ||   // fallback duration price
          item?.price || 0                 // base fallback
        )
      );
    }, 0);

    setTotalPrice(total);
  }, [lockerList]);

    return (
    <div className="booking-summary">
      <h2 className="title">
        {t("booking_summary_confirm_details_title")}
      </h2>

      <div className="location">
        {address}
      </div>

      {isPodLockerType ? (
        <div className="locker-list">
          {lockerName && (
            <div className="locker-card">
              <div className="locker-info">
                <span className="locker-name">{getLockerNameLabelText(isPodLockerType, t)}</span>
                <div className="time-select">
                  <p>{lockerName}</p>
                </div>
              </div>
            </div>
          )}

          <div className="locker-card">
            <div className="locker-info">
              <span className="locker-name">{t('start_time_label')}</span>
              <div className="time-select">
                <p>
                  {bookingTimingObject?.reserve_start
                    ? `${getFormatDate(bookingTimingObject.reserve_start)}  ${getFormatTime(bookingTimingObject.reserve_start)}`
                    : ""}
                </p>
              </div>
            </div>
          </div>

          <div className="locker-card">
            <div className="locker-info">
              <span className="locker-name">{t('end_time_label')}</span>
              <div className="time-select">
                <p>
                  {bookingTimingObject?.reserve_end
                    ? `${getFormatDate(bookingTimingObject.reserve_end)}  ${getFormatTime(bookingTimingObject.reserve_end)}`
                    : ""}
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="locker-list">
          <fieldset className="summary-fieldset">
            <legend className="summary-legend-header1">{t('booking_details_title')}</legend>
            {lockerList.map((locker) => (
              <div className="locker-card" key={locker.id}>
                <div className="locker-info">
                  <span className="locker-name">{locker.name}</span>

                  {locker?.bookingSlots ? (
                    <button className="choose-slot-btn" disabled>
                      {locker.selectedSlot
                        ? locker.selectedSlot.durationRange
                        : t("slot_not_selected")}
                    </button>
                  ) : (
                    <div className="time-select">
                      {locker.durations?.map((duration, index) => (
                        <p key={index}>
                          {duration.label}
                        </p>
                      ))}
                    </div>
                  )}

                  <span className="price">
                    {` ${getSymbolFromCurrency(lockerCurrency)} `}
                    {locker?.selectedSlot?.price ||
                      locker?.durations?.[0]?.price ||
                      locker?.price}
                  </span>
                </div>
              </div>
            ))}

            <div className="add-locker" onClick={handlerCLick}>
              {t("add_another_update_selected_locker", getUnitLabelParamsFromBool(isPodLockerType, t))}
            </div>

         <div className="price-summary">
              <div className="row subtotal">
                <span className="label">{t('text_subtotal')}</span>
                <span className="value">
                  {getSymbolFromCurrency(lockerCurrency)} {baseAmount.toFixed(2)}
                </span>
              </div>

              {collectGst && is_state_gst ? (
                <>
                  <div className="row">
                    <span className="label">CGST ({cgst_rate}%)</span>
                    <span className="value">
                      {getSymbolFromCurrency(lockerCurrency)} {cgstAmount.toFixed(2)}
                    </span>
                  </div>

                  <div className="row">
                    <span className="label">SGST ({sgst_rate}%)</span>
                    <span className="value">
                      {getSymbolFromCurrency(lockerCurrency)} {sgstAmount.toFixed(2)}
                    </span>
                  </div>
                </>
              ) : null}

              {collectGst && !is_state_gst ? (
                <div className="row">
                  <span className="label">IGST ({igst_rate}%)</span>
                  <span className="value">
                    {getSymbolFromCurrency(lockerCurrency)} {igstAmount.toFixed(2)}
                  </span>
                </div>
              ) : null}

              <div className="divider"></div>

              <div className="row total">
                <span className="label">{t("total_payable_amount")}</span>
                <span className="value total-price">
                  {getSymbolFromCurrency(lockerCurrency)} {finalTotal.toFixed(2)}
                </span>
              </div>
          </div>
          </fieldset>

          {isClientRefEnabled && (
            <fieldset className="summary-fieldset client-ref-fieldset">
              <legend className="summary-legend-header1">{t('additional_details_title')}</legend>
              <div className="client-ref-body">
                <label className="client-ref-label">
                  {clientRefLabel} <span className="required-asterisk">*</span>
                </label>
                <input
                  type="text"
                  className={`client-ref-input ${clientRefError ? "has-error" : ""}`}
                  value={clientRefInput}
                  onChange={handleClientRefChange}
                  placeholder={t('client_reference_placeholder', { value: clientRefLabel })}
                  maxLength={30}
                />
                {clientRefError && (
                  <span className="client-ref-error">{t('requiredFieldError')}</span>
                )}
              </div>
            </fieldset>
          )}
        </div>
      )}
      <div className="buttons">
        <ButtonComponent
          type="primary"
          handleClick={handlePayment}
          title={isPodLockerType ? t('confirm_booking_button') : t('proceed_to_pay')}
        />
      </div>

    </div>
  );
};

export default BookingSummary;
